import { ArrowLeftOutlined, LockOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Result, Skeleton, Typography } from "antd";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

import { ApiError } from "../api/client";
import { fetchProjectMetadata, type ProjectParty } from "../api/projects";
import {
  fetchReviewTaskCloudDriveInfo,
  fetchReviewTaskDetail,
  recordReviewTaskCloudDriveInfoCopy,
} from "../api/reviews";
import { CloudDriveInfoViewer } from "../features/projects/CloudDriveInfoViewer";
import { ProjectAttachmentWorkspace } from "../features/projects/ProjectAttachmentWorkspace";

function valueOrDash(value: string | null) {
  return value?.trim() || "—";
}

function partyNames(parties: ProjectParty[]) {
  return parties.map((party) => party.current_name).join("、") || "—";
}

export function ReviewTaskDetailPage() {
  const { assignmentId = "" } = useParams();
  const navigate = useNavigate();
  const detailQuery = useQuery({
    queryKey: ["review-task", assignmentId],
    queryFn: () => fetchReviewTaskDetail(assignmentId),
    enabled: Boolean(assignmentId),
    retry: false,
  });
  const metadataQuery = useQuery({
    queryKey: ["project-metadata"],
    queryFn: ({ signal }) => fetchProjectMetadata(signal),
    staleTime: 30 * 60_000,
  });
  const workTypeLabels = new Map(
    (metadataQuery.data?.work_types ?? []).map((item) => [item.code, item.label]),
  );
  const rightsModeLabels = new Map(
    (metadataQuery.data?.rights_modes ?? []).map((item) => [item.code, item.label]),
  );

  if (detailQuery.isPending) {
    return (
      <main className="review-task-detail-page">
        <Skeleton active paragraph={{ rows: 16 }} />
      </main>
    );
  }
  if (detailQuery.isError || !detailQuery.data) {
    const forbidden = detailQuery.error instanceof ApiError && detailQuery.error.status === 403;
    return (
      <main className="review-task-detail-page">
        <Result
          status={forbidden ? "403" : "404"}
          title={forbidden ? "无权查看该审核任务" : "审核任务不存在或已被撤销"}
          extra={<Button onClick={() => void navigate("/review-tasks")}>返回审核任务</Button>}
        />
      </main>
    );
  }

  const detail = detailQuery.data;
  const project = detail.project;

  return (
    <main className="review-task-detail-page">
      <nav className="project-detail-breadcrumb" aria-label="面包屑">
        <button type="button" onClick={() => void navigate("/review-tasks")}>
          审核任务
        </button>
        <span>/</span>
        <strong>任务详情</strong>
      </nav>
      <button
        className="review-task-detail-back"
        type="button"
        onClick={() => void navigate("/review-tasks")}
      >
        <ArrowLeftOutlined /> 返回审核任务
      </button>

      <header className="review-task-detail-header">
        <div>
          <Typography.Title level={1}>{project.work_name}</Typography.Title>
          <p>
            项目编号：{project.project_number}
            <i>·</i>分配于 {dayjs(detail.assigned_at).format("YYYY-MM-DD")}
            <i>·</i>最后更新 {dayjs(project.updated_at).format("YYYY-MM-DD HH:mm")}
          </p>
        </div>
        <span className="review-task-readonly-badge">
          <LockOutlined /> 只读查看
        </span>
      </header>

      <div className="review-task-detail-layout">
        <aside className="review-task-project-column">
          <section className="review-task-project-info">
            <header>
              <Typography.Title level={2}>项目信息</Typography.Title>
              <span>仅查看</span>
            </header>
            <dl>
              <div>
                <dt>作品名称</dt>
                <dd>{project.work_name}</dd>
              </div>
              <div>
                <dt>作品类型</dt>
                <dd>{workTypeLabels.get(project.work_type_code) ?? project.work_type_code}</dd>
              </div>
              <div>
                <dt>权利归属方式</dt>
                <dd>
                  {rightsModeLabels.get(project.rights_mode_code) ?? project.rights_mode_code}
                </dd>
              </div>
              <div>
                <dt>创作完成日期</dt>
                <dd>{project.completion_date}</dd>
              </div>
              <div>
                <dt>创作发表日期</dt>
                <dd>{project.publication_date ?? "未发表"}</dd>
              </div>
              <div>
                <dt>著作权人</dt>
                <dd>{partyNames(project.right_holders)}</dd>
              </div>
              <div>
                <dt>作者</dt>
                <dd>{partyNames(project.authors)}</dd>
              </div>
              <div>
                <dt>网盘信息</dt>
                <dd>
                  {project.cloud_drive_info?.trim() ? (
                    <CloudDriveInfoViewer
                      loadInfo={() => fetchReviewTaskCloudDriveInfo(assignmentId)}
                      recordCopy={() => recordReviewTaskCloudDriveInfoCopy(assignmentId)}
                    />
                  ) : (
                    "—"
                  )}
                </dd>
              </div>
            </dl>
          </section>

          <section className="review-task-project-notes">
            <div>
              <Typography.Title level={3}>作品简介</Typography.Title>
              <p>{valueOrDash(project.work_summary)}</p>
            </div>
            <div>
              <Typography.Title level={3}>创作过程</Typography.Title>
              <p>{valueOrDash(project.creation_process)}</p>
            </div>
          </section>
        </aside>

        <section className="review-task-attachments" aria-label="项目附件">
          <ProjectAttachmentWorkspace
            projectId={project.id}
            canUpload={false}
            canManage={false}
            attachments={detail.attachments}
          />
        </section>
      </div>

      <div className="review-task-detail-note">
        <SafetyCertificateOutlined />
        审核任务仅供查看，项目资料及附件不可修改
      </div>
    </main>
  );
}
